import { useCallback, useRef, useState } from 'react';

export interface UseFormErrors<K extends string> {
  errors: Partial<Record<K, string>>;
  setErrors: (errors: Partial<Record<K, string>>) => void;
  clearError: (key: K) => void;
  hasErrors: boolean;
  focusFirstInvalid: () => void;
  registerField: (key: K) => (el: HTMLElement | null) => void;
}

export function useFormErrors<K extends string>(): UseFormErrors<K> {
  const [errors, setErrorsState] = useState<Partial<Record<K, string>>>({});
  const refs = useRef<Partial<Record<K, HTMLElement>>>({});

  const setErrors = useCallback((next: Partial<Record<K, string>>): void => {
    setErrorsState(next);
  }, []);

  const clearError = useCallback((key: K): void => {
    setErrorsState((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const registerField = useCallback(
    (key: K) =>
      (el: HTMLElement | null): void => {
        if (el) refs.current[key] = el;
        else delete refs.current[key];
      },
    [],
  );

  const focusFirstInvalid = useCallback((): void => {
    setErrorsState((current) => {
      const first = Object.keys(current).find((k) => current[k as K]) as
        | K
        | undefined;
      if (first) {
        const el = refs.current[first];
        if (el) {
          const target =
            el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
              ? el
              : el.querySelector<HTMLElement>('input, textarea, select') ?? el;
          target.focus();
          if (typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
      return current;
    });
  }, []);

  return {
    errors,
    setErrors,
    clearError,
    hasErrors: Object.keys(errors).length > 0,
    focusFirstInvalid,
    registerField,
  };
}
